/**
 * OTP modal UI. The existing `#otpOverlay` DOM in index.html stays as-is;
 * this module wires it to the new `otp.service.js`.
 *
 * Returns a Promise that resolves with `{ verified: true }` when the user
 * enters the correct code, or `{ verified: false, cancelled: true }` if they
 * close the modal.
 */

import * as otp from './otp.service.js';
import { format as formatPhone } from '../../shared/phone.js';
import { createLogger } from '../../core/logger.js';
import * as alert from '../../core/alert.js';
import * as spinner from '../../core/spinner.js';

const log = createLogger('otp.view');

const IDS = /** @type {const} */ ({
  overlay: 'otpOverlay',
  closeBtn: 'otpCloseBtn',
  cancelBtn: 'otpCancelBtn',
  verifyBtn: 'otpVerifyBtn',
  codeInput: 'otpCode',
  phoneLabel: 'otpPhoneLabel',
  error: 'otpError',
  resendBtn: 'otpResendBtn',
  countdown: 'otpCountdown',
});

/**
 * Run the OTP flow: send the code, show the modal, resolve when verified or
 * cancelled. Multiple concurrent calls are not supported — the second one
 * will reject.
 *
 * @param {Object} args
 * @param {string} args.phone
 * @returns {Promise<{ verified: boolean; cancelled?: boolean }>}
 */
export async function runOtpFlow({ phone }) {
  if (running) throw new Error('OTP flow already in progress');
  running = true;
  try {
    return await runInternal(phone);
  } finally {
    running = false;
  }
}

let running = false;

/**
 * @param {string} phone
 * @returns {Promise<{ verified: boolean; cancelled?: boolean }>}
 */
async function runInternal(phone) {
  const overlay = document.getElementById(IDS.overlay);
  if (!overlay) {
    log.error('otp overlay element missing');
    return { verified: false, cancelled: true };
  }

  try {
    await otp.send(phone);
  } catch (err) {
    await alert.showAlert({
      title: 'שגיאה בשליחת SMS',
      message: /** @type {Error} */ (err).message || 'נסה שוב מאוחר יותר',
    });
    return { verified: false, cancelled: true };
  }

  const phoneLabel = document.getElementById(IDS.phoneLabel);
  if (phoneLabel) phoneLabel.textContent = formatPhone(phone);

  showError('');
  const codeInput = /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.codeInput));
  if (codeInput) codeInput.value = '';

  openOverlay(overlay);

  // While the OTP overlay is interactive the parent flow's spinner
  // ("מתחבר...") is technically still acquired by login.view.js — keeping
  // it visible behind the modal makes the app feel frozen for the seconds /
  // minutes the user spends reading the SMS and typing. Suspend the spinner
  // for the duration of the modal; the resume function fires from
  // `closeAndResolve` so the spinner returns automatically if the parent
  // flow still has work to do (e.g. transferOwnershipToCurrentUser).
  const resumeSpinner = spinner.suspend();

  return new Promise((resolve) => {
    /** @type {(() => void)[]} */
    const detachers = [];
    const detachAll = () => { detachers.forEach((d) => d()); };

    const closeAndResolve = (/** @type {{ verified: boolean; cancelled?: boolean }} */ result) => {
      detachAll();
      closeOverlay(overlay);
      otp.reset();
      resumeSpinner();
      resolve(result);
    };

    const onVerifyClick = async () => {
      const code = (codeInput?.value || '').trim();
      const verifyBtn = document.getElementById(IDS.verifyBtn);
      if (verifyBtn) /** @type {HTMLButtonElement} */ (verifyBtn).disabled = true;
      try {
        const result = await otp.verify(code);
        if (result.valid) {
          closeAndResolve({ verified: true });
        } else {
          showError(result.reason);
        }
      } finally {
        if (verifyBtn) /** @type {HTMLButtonElement} */ (verifyBtn).disabled = false;
      }
    };

    const onCancelClick = () => closeAndResolve({ verified: false, cancelled: true });

    detachers.push(wireClick(IDS.verifyBtn, () => { void onVerifyClick(); }));
    detachers.push(wireClick(IDS.cancelBtn, onCancelClick));
    detachers.push(wireClick(IDS.closeBtn, onCancelClick));

    if (codeInput) {
      const onEnter = (/** @type {KeyboardEvent} */ e) => {
        if (e.key === 'Enter') void onVerifyClick();
      };
      codeInput.addEventListener('keydown', onEnter);
      detachers.push(() => codeInput.removeEventListener('keydown', onEnter));
      codeInput.focus();
    }
  });
}

/**
 * @param {HTMLElement} overlay
 * The OTP overlay is styled with two CSS rules: `#otpOverlay { display:none }`
 * and `#otpOverlay.open { display:flex }`. Plus the general `.terms-overlay`
 * rule sets `opacity:0` and `.terms-overlay.show` sets `opacity:1`. Both
 * classes are needed for the dialog to appear and animate in.
 */
function openOverlay(overlay) {
  overlay.setAttribute('aria-hidden', 'false');
  overlay.classList.add('open', 'show');
  overlay.style.display = 'flex';
  overlay.style.opacity = '1';
  document.body.classList.add('no-scroll');
}

/** @param {HTMLElement} overlay */
function closeOverlay(overlay) {
  overlay.setAttribute('aria-hidden', 'true');
  overlay.classList.remove('open', 'show');
  overlay.style.opacity = '0';
  setTimeout(() => {
    if (overlay.getAttribute('aria-hidden') === 'true') {
      overlay.style.display = 'none';
    }
  }, 300);
  document.body.classList.remove('no-scroll');
}

/** @param {string} id @param {() => void} handler */
function wireClick(id, handler) {
  const el = document.getElementById(id);
  if (!el) return () => {};
  el.addEventListener('click', handler);
  return () => el.removeEventListener('click', handler);
}

/** @param {string} message */
function showError(message) {
  const el = document.getElementById(IDS.error);
  if (!el) return;
  if (message) {
    el.textContent = message;
    el.style.display = 'block';
  } else {
    el.textContent = '';
    el.style.display = 'none';
  }
}
